// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { AppConfig } from '../../config/config';
import { SnackMessage } from '../../config/constant';
import { APIService } from '../../utils/apiService';
import {
  ExamSubmissionRequestInterface,
  SubmitAnswerSheetState,
} from '../../utils/types';
import { enqueueSnackbarMessage } from '../commonSlice/common';

const initialState: SubmitAnswerSheetState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: null,
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const saveAnswerSheet = createAsyncThunk(
  "exams/saveAnswerSheet",
  async (answerSheet: ExamSubmissionRequestInterface, { dispatch }) => {
    return new Promise<{ answerSheet: any }>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.saveExamPaper, answerSheet)
        .then((resp) => {
          if (resp.status == 201) {
            resolve({ answerSheet: resp.data });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.saveAnswerSheet,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

export const submitAnswerSheet = createAsyncThunk(
  "exams/submitAnswerSheet",
  async (answerSheet: ExamSubmissionRequestInterface, { dispatch, rejectWithValue }) => {
    return new Promise<{ answerSheet: any }>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.submitExamPaper, answerSheet)
        .then((resp) => {
          if (resp.status == 201) {
            resolve({ answerSheet: resp.data });
          }
          if (resp.status == 409) {
            return rejectWithValue({ message: "Conflict: Exam already submitted", status: 409 });
          }
          else {
            return rejectWithValue({ message: "Something went wrong", status: resp.status });
          }
        })
        .catch((error: Error) => {
          let isAlreadySubmitted = false;
          if ((error as any).response?.status === 409) {
            isAlreadySubmitted = true;
          }
          dispatch(
            enqueueSnackbarMessage({
              message: isAlreadySubmitted
                ? SnackMessage.error.alreadySubmittedAnswerSheet
                : SnackMessage.error.submitAnswerSheet,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

export const finishPracticalExam = createAsyncThunk(
  "exams/finishPracticalExam",
  async (payload: any, { dispatch }) => {
    return new Promise<{ submissionState: any }>((resolve, reject) => {
      APIService.getInstance()
        .patch(
          `${AppConfig.serviceUrls.finishPracticalExam}/${payload.examId}`,
          payload.paperDetails
        )
        .then((resp) => {
          if (resp.status == 200) {
            resolve({ submissionState: resp.data });
          } else {
            reject("Something went wrong");
          }
        })
        .catch((error: Error) => {
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.submitAnswerSheet,
              type: "error",
            })
          );
          reject(error);
        });
    });
  }
);

const answerSheetSlice = createSlice({
  name: "answerSheet",
  initialState,
  reducers: {
    setState: (
      state,
      action: PayloadAction<SubmitAnswerSheetState["state"]>
    ) => {
      state.state = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitAnswerSheet.pending, (state, action) => {
        state.stateMessage = "Submitting answer sheet";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Submitting answer sheet";
      })
      .addCase(submitAnswerSheet.fulfilled, (state, action) => {
        state.response = action.payload.answerSheet;
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(submitAnswerSheet.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      })

      .addCase(finishPracticalExam.pending, (state, action) => {
        state.stateMessage = "Finishing exam";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Submitting answer sheet";
      })
      .addCase(finishPracticalExam.fulfilled, (state, action) => {
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(finishPracticalExam.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export const { setState } = answerSheetSlice.actions;
export default answerSheetSlice.reducer;
